import { FC, HTMLAttributes } from "react";
import { useSelector } from "react-redux";
import * as _ from "lodash";

import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { HerdGroupEntity, Enterprise, FarmEntity, EnterpriseAppState, HerdGroupList } from "@ctra/api";
import { Charts } from "@ctra/components";
import { useGoogleAnalytics } from "@ctra/analytics";

import { GAActions } from "../../analytics";

const { MultiDropdown } = Charts;

interface HerdGroupFilterProps {
  /**
   * Allow multiple selections
   */
  multiple?: boolean;
  /**
   * selected herd group(s)
   */
  selection: Array<HerdGroupEntity["id"]>;
  /**
   * handle herd group change
   */
  onHerdGroupChange: (herdGroups: Array<HerdGroupEntity["id"]>) => void;
  /**
   * list of farm ids to pre-filter the herd groups against
   */
  farmIDList?: Array<FarmEntity["id"]>;
  /**
   * disabled state
   */
  disabled?: boolean;
}

/**
 * Herd group filtering
 * @param {boolean | undefined} multiple
 * @param {Array<HerdGroupEntity["id"]>} selection
 * @param {Array<FarmEntity["id"]> | undefined} farmIDList
 * @param {(herdGroups: Array<HerdGroupEntity["id"]>) => void} onHerdGroupChange
 * @param {boolean | undefined} disabled
 * @param {Pick<HerdGroupFilterProps & Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> & {children?: React.ReactNode | undefined}, "children" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "className" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const HerdGroupFilter: FC<HerdGroupFilterProps & Omit<HTMLAttributes<HTMLDivElement>, "onChange">> = ({
  multiple = false,
  selection,
  farmIDList = [],
  onHerdGroupChange,
  disabled,
  ...rest
}) => {
  const { t } = useTranslation();
  const { trackEvent, label } = useGoogleAnalytics<{ label: string }>();

  /**
   * Fetch the herd groups which are available for the given farms
   */
  const herdGroups = useSelector<EnterpriseAppState, HerdGroupList>((state) =>
    Enterprise.entities.getHerdGroups(state, { farmID: farmIDList })
  );

  return (
    <MultiDropdown<HerdGroupEntity["id"]>
      disabled={disabled}
      limit={multiple ? null : 1}
      label={t<string>(Content.chart.filters.herdGroup.label)}
      values={_.map(
        _.sortBy(herdGroups, (group) => {
          /**
           * some of the group names are numeric strings, we pad them so, they can be ordered lexicographically
           */
          return _.isInteger(_.parseInt(group.name)) ? _.padStart(group.name, 4) : group.name;
        }),
        "id"
      )}
      selection={selection}
      onChange={(values) => {
        /* istanbul ignore next */
        trackEvent(GAActions.selectHerdGroup, { label: `${label} - ${values.join(",")}` });
        onHerdGroupChange(values as Array<HerdGroupEntity["id"]>);
      }}
      filterOption={(input, option) => {
        return _.includes(_.toLower(option?.children), _.toLower(input));
      }}
      getLabel={(herdGroupID) => t<string>(Content.chart.filters.herdGroup.single, herdGroups[herdGroupID])}
      placeholder={t<string>(Content.chart.filters.herdGroup.placeholder)}
      {...rest}
    />
  );
};

export default HerdGroupFilter;
